import { IconAlertCircle, IconLayoutGrid, IconSparkles, IconUsers } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import type { HomeData } from "../use-home-data";

type KpiCardsProps = {
  businessType: "restaurant" | "supermarket";
  kpis: HomeData["kpis"];
  subscription: HomeData["subscription"];
};

const TIER_FALLBACK = "Free";

export function KpiCards({ businessType, kpis, subscription }: KpiCardsProps) {
  const { t } = useTranslation();
  const isSupermarket = businessType === "supermarket";

  const subTier =
    subscription?.productName ??
    (subscription ? t("dashboard.subscription.activePlan") : TIER_FALLBACK);
  const subStatus = subscription?.status ?? "inactive";
  const subBadgeVariant: "default" | "outline" | "secondary" | "destructive" =
    subStatus === "active"
      ? "default"
      : subStatus === "past_due" || subStatus === "canceled"
        ? "destructive"
        : "secondary";

  return (
    <div className="grid grid-cols-1 gap-4 px-4 @xl/main:grid-cols-2 @5xl/main:grid-cols-4 lg:px-6 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card *:data-[slot=card]:shadow-xs dark:*:data-[slot=card]:bg-card">
      <Card className="@container/card">
        <CardHeader>
          <CardDescription>
            {isSupermarket
              ? t("dashboard.kpi.catalogProducts", {
                  defaultValue: "Productos del catálogo",
                })
              : t("dashboard.kpi.products")}
          </CardDescription>
          <CardTitle className="font-semibold text-2xl tabular-nums @[250px]/card:text-3xl">
            {kpis.products}
          </CardTitle>
          <Badge className="ml-auto" variant="outline">
            <IconLayoutGrid className="size-3.5" />
            {isSupermarket
              ? t("dashboard.kpi.catalogCategoriesBadge", {
                  count: kpis.visibleSections,
                  defaultValue: "{{count}} categorías visibles",
                })
              : t("dashboard.kpi.productsBadge", { count: kpis.visibleSections })}
          </Badge>
        </CardHeader>
        <CardFooter className="flex-col items-start gap-1.5 text-sm">
          {kpis.products === 0 ? (
            <Link className="font-medium text-primary hover:underline" to="/menu/products/new">
              {t("dashboard.kpi.productsEmptyCta")}
            </Link>
          ) : (
            <div className="text-muted-foreground">
              {isSupermarket
                ? t("dashboard.kpi.catalogProductsSub", {
                    defaultValue: "Ítems actualmente en tu catálogo",
                  })
                : t("dashboard.kpi.productsSub")}
            </div>
          )}
        </CardFooter>
      </Card>

      <Card className="@container/card">
        <CardHeader>
          <CardDescription>{t("dashboard.kpi.promotions")}</CardDescription>
          <CardTitle className="font-semibold text-2xl tabular-nums @[250px]/card:text-3xl">
            {kpis.activePromotions}
          </CardTitle>
          {kpis.expiringSoon > 0 && (
            <Badge className="ml-auto" variant="outline">
              <IconAlertCircle className="size-3.5" />
              {t("dashboard.kpi.promotionsExpiring", {
                count: kpis.expiringSoon,
              })}
            </Badge>
          )}
        </CardHeader>
        <CardFooter className="flex-col items-start gap-1.5 text-sm">
          {kpis.activePromotions === 0 ? (
            <Link className="font-medium text-primary hover:underline" to="/menu/promotions">
              {t("dashboard.kpi.promotionsEmptyCta")}
            </Link>
          ) : (
            <div className="text-muted-foreground">{t("dashboard.kpi.promotionsSub")}</div>
          )}
        </CardFooter>
      </Card>

      <Card className="@container/card">
        <CardHeader>
          <CardDescription>{t("dashboard.kpi.team")}</CardDescription>
          <CardTitle className="font-semibold text-2xl tabular-nums @[250px]/card:text-3xl">
            {kpis.members}
          </CardTitle>
          {kpis.pendingInvites > 0 && (
            <Badge className="ml-auto" variant="secondary">
              <IconUsers className="size-3.5" />
              {t("dashboard.kpi.teamPending", { count: kpis.pendingInvites })}
            </Badge>
          )}
        </CardHeader>
        <CardFooter className="flex-col items-start gap-1.5 text-sm">
          <Link className="font-medium text-primary hover:underline" to="/organizations">
            {t("dashboard.kpi.teamManage")}
          </Link>
        </CardFooter>
      </Card>

      <Card className="@container/card">
        <CardHeader>
          <CardDescription>{t("dashboard.kpi.subscription")}</CardDescription>
          <CardTitle className="font-semibold text-2xl capitalize tabular-nums @[250px]/card:text-3xl">
            {subTier}
          </CardTitle>
          <Badge className="ml-auto capitalize" variant={subBadgeVariant}>
            <IconSparkles className="size-3.5" />
            {t(`dashboard.subscription.status.${subStatus}`)}
          </Badge>
        </CardHeader>
        <CardFooter className="flex-col items-start gap-1.5 text-sm">
          <Link className="font-medium text-primary hover:underline" to="/billing">
            {t("dashboard.subscription.manage")}
          </Link>
        </CardFooter>
      </Card>
    </div>
  );
}
